import React, { useEffect } from "react";
import { X, MapPin, Calendar } from "lucide-react";

const Avatar = ({ name, imageUrl }) => {
  const [imgError, setImgError] = React.useState(false);
  const initials = (name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        onError={() => setImgError(true)}
        className="w-full h-full object-cover"
      />
    );
  }
  return (
    <div className="w-full h-full bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center text-3xl font-bold text-gray-900">
      {initials || "Ü"}
    </div>
  );
};

export default function CelebDetailModal({ person, onClose }) {
  const isPolitician = person.type === "politician";

  /* ESC ile kapat */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  /* Scroll kilitle */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!person) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kapat butonu */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-slate-500 hover:text-slate-800 shadow-sm transition"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Üst kısım — fotoğraf + temel bilgiler */}
        <div className="flex gap-4 p-5 pb-4 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
          {/* Fotoğraf */}
          <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100">
            <Avatar name={person.name} imageUrl={person.imageUrl} />
          </div>

          {/* İsim + rozetler */}
          <div className="flex-1 min-w-0 pt-1">
            <h2 className="font-bold text-slate-900 text-lg leading-tight">
              {person.name}
            </h2>

            <div className="flex flex-wrap gap-1 mt-1.5">
              {/* Doğum yılı */}
              {person.born && (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  <Calendar className="w-3 h-3" /> {person.born}
                </span>
              )}
              {/* Cinsiyet */}
              {person.gender && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  {person.gender}
                </span>
              )}
              {/* Durum */}
              {person.isDeceased && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 font-medium">
                  Vefat
                </span>
              )}
              {person.isActive && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  Görevde
                </span>
              )}
              {person.isPassive && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                  Görevde Değil
                </span>
              )}
            </div>

            {/* Meslek / Görev */}
            <p className="text-sm text-amber-700 font-semibold mt-1.5 truncate">
              {person.profession || "—"}
            </p>

            {/* Parti (siyasetçi) */}
            {isPolitician && person.party && (
              <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-medium">
                {person.party}
              </span>
            )}
          </div>
        </div>

        {/* Scroll içerik */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Konum */}
          {(person.hometown || person.koy) && (
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">
                  Köken
                </p>
                <p className="text-sm text-slate-700">
                  {[person.hometown, person.koy].filter(Boolean).join(" / ")}
                </p>
              </div>
            </div>
          )}

          {/* Biyografi */}
          {person.bio && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                Biyografi
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                {person.bio}
              </p>
            </div>
          )}

          {/* Siyasetçi ek bilgiler */}
          {isPolitician && (
            <div className="grid grid-cols-2 gap-3">
              {person.party && (
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
                    Parti
                  </p>
                  <p className="text-sm font-medium text-slate-800">
                    {person.party}
                  </p>
                </div>
              )}
              {person.profession && (
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">
                    Görev
                  </p>
                  <p className="text-sm font-medium text-slate-800">
                    {person.profession}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Etiketler */}
          {person.tags?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                Etiketler
              </p>
              <div className="flex flex-wrap gap-1.5">
                {person.tags.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
